import React, {useEffect, useMemo, useRef, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, useIntl} from 'react-intl'

// Components
import {
    Box,
    Text,
    useMultiStyleConfig,
    Button,
    Select,
    Image,
    Skeleton as ChakraSkeleton,
    useStyleConfig,
    IconButton,
    Stack
} from '@salesforce/retail-react-app/app/components/shared/ui'

import DynamicImage from '../../dynamic-image'
import DisplayPrice from '../../display-price'
import SwatchGroup from '../../swatch-group'
import Swatch from '../../swatch-group/swatch'

import {useCurrency} from '@salesforce/retail-react-app/app/hooks/use-currency'
import {useDerivedProduct} from '../../../hooks/use-derived-product'

import {filterImageGroups, getPriceData} from '../../../utils/product-utils'
import {formattedURL} from '../../../utils/url'
import {getUrl} from '../../../utils/cloudinary-utils'

import {
    PRODUCT_TILE_IMAGE_VIEW_TYPE,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_3,
    PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
} from '../../../constants'

import QuickAddChild from './quick-add-child'
import {useAccessToken, useCommerceApi} from '@salesforce/commerce-sdk-react'
import {useGlobaleContext} from '../../../contexts/globale'
import withRegistration from '../../with-registration'
import WishlistAnimation from '../../product-tile/partials/wishlist-animation'
const IconButtonWithRegistration = withRegistration(IconButton)

// -----------------------------------------------------------------------------
// Constants
// -----------------------------------------------------------------------------
const dynamicImageProps = {
    imageProps: {
        loading: 'lazy',
        fetchpriority: 'auto'
    },
    widths: ['225w', '325w', '384w', '576w', '650w', '768w', '960w']
}

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------
const ChildProduct = ({
    product: productOriginal,
    addToCart,
    isBasketLoading,
    setIsChildLoading,
    setChildProductSelection,
    showAddedCta,
    customer = {},
    onFavouriteToggle,
    index: childIndex,
    wishlistAnimation = false,
    parentProduct = {},
    wishlist = {}
}) => {
    const {countryISO, currencyCode, globaleCurrency} = useGlobaleContext()
    const isFavouriteLoading = useRef(false)
    const intl = useIntl()
    const {currency} = useCurrency()
    const api = useCommerceApi()
    const {getTokenWhenReady} = useAccessToken()
    const formStyles = useStyleConfig('FormStyles')
    const [product, setProduct] = useState(productOriginal)
    const [showPriceSkeleton, setShowPriceSkeleton] = useState(true)
    const [shouldFetchPriceData, setShouldFetchPriceData] = useState(false)

    const {imageGroups, variants, c_colorMaterial, c_color} = product || {}
    const styles = useMultiStyleConfig('ProductTileStyles')

    useEffect(() => {
        setProduct(productOriginal)
    }, [productOriginal?.c_selectedCurrency])

    // Detect selectable attribute (stoneColor, metal, letter, carat)
    const selectableAttributeId = product?.variationAttributes?.find((attr) =>
        [
            PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_1,
            PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_2,
            PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_3,
            PRODUCT_TILE_SELECTABLE_ATTRIBUTE_ID_4
        ]?.includes(attr?.id)
    )?.id

    const initialVariationValue =
        variants?.find((v) => v.productId === product.id)?.variationValues?.[
            selectableAttributeId
        ] ||
        variants?.[0]?.variationValues?.[selectableAttributeId] ||
        c_colorMaterial ||
        c_color

    const [selectableAttributeValue, setSelectableAttributeValue] = useState(initialVariationValue)

    useEffect(() => {
        if (initialVariationValue) setSelectableAttributeValue(initialVariationValue)
    }, [initialVariationValue])

    useEffect(() => {
        setTimeout(() => {
            setShowPriceSkeleton(
                !globaleCurrency || globaleCurrency != parentProduct?.c_selectedCurrency
            )
        }, 300)
    }, [globaleCurrency, parentProduct?.c_selectedCurrency])

    // -------------------------------------------------------------------------
    // Image Handling
    // -------------------------------------------------------------------------
    const image = useMemo(() => {
        const hasSelectableAttribute = product?.variationAttributes?.find(
            ({id}) => id === selectableAttributeId
        )

        const variationValues = {[selectableAttributeId]: selectableAttributeValue}

        const filteredImageGroups = filterImageGroups(imageGroups, {
            viewType: PRODUCT_TILE_IMAGE_VIEW_TYPE,
            variationValues: hasSelectableAttribute ? variationValues : {}
        })

        const defaultImageGroups = filterImageGroups(imageGroups, {
            viewType: PRODUCT_TILE_IMAGE_VIEW_TYPE
        })

        return filteredImageGroups?.[0]?.images?.[0] ?? defaultImageGroups?.[0]?.images?.[0]
    }, [product, selectableAttributeId, selectableAttributeValue])

    const imageProps = {
        background: 'gray.100',
        widths: dynamicImageProps.widths,
        imgSizes: '(min-width: 80em) 25vw, (min-width: 62em) 25vw, (min-width: 48em) 25vw, 50vw',
        imageProps: {
            alt: product.name || product.productName,
            loading: dynamicImageProps.imageProps.loading,
            height: 'auto',
            aspectRatio: '185.5/236',
            width: '100%',
            mixBlendMode: 'multiply',
            ...dynamicImageProps.imageProps
        }
    }

    // -------------------------------------------------------------------------
    // Pricing
    // -------------------------------------------------------------------------
    const salePriceColor =
        product?.c_promotions?.find((p) => p?.textColor)?.textColor || 'green.500'

    const [productWithFilteredVariants, setProductWithFilteredVariants] = useState(() => {
        const filtered = product?.variants?.filter(
            ({variationValues, productId}) =>
                variationValues?.[selectableAttributeId] === selectableAttributeValue &&
                productId === product?.id
        )
        return filtered?.length ? {...product, variants: filtered} : product
    })

    useEffect(() => {
        const filtered = product?.variants?.filter(
            ({variationValues, productId}) =>
                variationValues?.[selectableAttributeId] === selectableAttributeValue &&
                productId === product?.id
        )
        setProductWithFilteredVariants(
            filtered?.length ? {...product, variants: filtered} : product
        )
    }, [product?.c_selectedCurrency, currencyCode])

    const [priceData, setPriceData] = useState(getPriceData(productWithFilteredVariants))

    useEffect(() => {
        if (productWithFilteredVariants) {
            const price = getPriceData(productWithFilteredVariants)
            setPriceData(price)
        }
    }, [productWithFilteredVariants])

    // -------------------------------------------------------------------------
    // Variant selection
    // -------------------------------------------------------------------------
    const [selectedVariant, setSelectedVariant] = useState(
        product?.variants?.find(
            ({variationValues, productId}) =>
                productId === product.id &&
                variationValues?.[selectableAttributeId] === selectableAttributeValue
        ) ||
            product?.variants?.find(
                ({variationValues, productId}) =>
                    variationValues?.[selectableAttributeId] === selectableAttributeValue &&
                    product?.c_allPdpLinks?.some((p) => p.id === productId)
            )
    )

    // -------------------------------------------------------------------------
    // URL
    // -------------------------------------------------------------------------
    const productUrl = product?.productId
        ? product?.c_allPdpLinks?.find((p) => p.id === selectedVariant?.productId)?.url
        : product?.c_url

    const productUrlFormatted = formattedURL(productUrl, false)
    const localizedProductName = product.name ?? product.productName

    const [hideMoreSwatches, setHideMoreSwatches] = useState(true)
    const {variant, variationAttributes, variationParams} = useDerivedProduct(
        product,
        true,
        false,
        false
    )

    useEffect(() => {
        const fetchVariant = async () => {
            if (!product || !selectableAttributeId || !selectableAttributeValue) return

            let variants = product.variants?.filter(
                ({variationValues, productId}) =>
                    variationValues?.[selectableAttributeId] === selectableAttributeValue &&
                    product?.c_allPdpLinks?.find((p) => p.id === productId) &&
                    productId === product?.id
            )
            if (!variants?.length) {
                variants = [
                    product.variants?.find(
                        ({variationValues, productId}) =>
                            variationValues?.[selectableAttributeId] === selectableAttributeValue &&
                            product?.c_allPdpLinks?.find((p) => p.id === productId)
                    )
                ]
            }

            if (shouldFetchPriceData) {
                if (!variants.length) {
                    const filteredProduct = {
                        ...product,
                        variants
                    }

                    setProductWithFilteredVariants(filteredProduct)
                    setChildProductSelection((prev) => ({
                        ...prev,
                        [product.id]: {
                            selectedSku: filteredProduct,
                            quantity: 1
                        }
                    }))

                    return
                }

                const token = await getTokenWhenReady()
                const requestParams = {id: variants[0].productId}

                if (countryISO && currencyCode) {
                    requestParams.c_selectedCountry = countryISO
                    requestParams.c_selectedCurrency = currencyCode
                }
                setShowPriceSkeleton(true)
                try {
                    setIsChildLoading(true)
                    const variantResponse = await api.shopperProducts.getProduct({
                        parameters: requestParams,
                        expand: ['promotions', 'prices', 'availability'],
                        headers: {
                            Authorization: `Bearer ${token}`
                        }
                    })

                    const filteredProduct = {
                        ...variantResponse,
                        tieredPrices: variants[0].tieredPrices
                    }

                    setProductWithFilteredVariants(filteredProduct)
                    const selectedVariant = {
                        ...variants[0],
                        productPromotions: variantResponse.productPromotions
                    }
                    setChildProductSelection((prev) => ({
                        ...prev,
                        [product.id]: {
                            selectedSku: selectedVariant,
                            quantity: 1
                        }
                    }))

                    setShouldFetchPriceData(false)
                    setShowPriceSkeleton(false)
                    setIsChildLoading(false)
                } catch (e) {
                    setShowPriceSkeleton(false)
                    setIsChildLoading(false)
                }
                return
            }
        }

        fetchVariant()
    }, [
        product,
        selectableAttributeId,
        selectableAttributeValue,
        shouldFetchPriceData,
        countryISO,
        currencyCode
    ])

    useEffect(() => {
        isFavouriteLoading.current = false // If user logged in, then refresh the current reference.
    }, [customer?.isRegistered, selectedVariant?.productId])

    const isFavourite = !!wishlist?.customerProductListItems?.find((item) =>
        product?.productId ? product?.productId === item.productId : product?.id === item.productId
    )

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    return (
        <Box
            className="combo-product"
            layerStyle="fullPage"
            data-testid="child-combo-product"
            background="white"
            sx={{
                '.product-name': {margin: '20px 10px 5px 0'},
                '.display-price-wrapper': {margin: '0 0 10px 0'}
            }}
            position="relative"
        >
            {/* Image + Hover CTA */}
            <Box
                marginBottom="20px"
                role="group"
                {...productUrlFormatted}
                className="child-details"
            >
                <Box position="relative">
                    <DynamicImage
                        data-testid="child-product-image"
                        display="block"
                        src={image?.disBaseLink || '/noimage.png'}
                        {...imageProps}
                    />

                    <Box
                        position="absolute"
                        top="0"
                        left="0"
                        w="100%"
                        h="100%"
                        display="flex"
                        className="quick-add-price-wrapper"
                        justifyContent="center"
                        alignItems="center"
                        opacity={{base: 1, lg: 0}} // visible on mobile, hidden by default on desktop
                        transition="opacity 0.2s ease"
                        _groupHover={{opacity: {base: 1, lg: 1}}} // show on hover for desktop
                    >
                        {!parentProduct?.c_hideAddToBag && (
                            <QuickAddChild
                                product={product}
                                isBasketLoading={isBasketLoading}
                                showAddedCtaForParent={showAddedCta}
                                selectedVariant={variant}
                                customer={customer}
                                wishlist={wishlist}
                                variationParams={variationParams}
                                priceData={priceData}
                                addToCart={addToCart}
                            />
                        )}
                    </Box>
                </Box>
                {!parentProduct?.c_hideFavoriteButton && (
                    <Box
                        opacity={{base: 1, lg: 0}} // visible on mobile, hidden by default on desktop
                        transition="opacity 0.2s ease"
                        _groupHover={{opacity: {base: 1, lg: 1}}} // show on hover for desktop
                    >
                        {/** NOTE: Always hide wishlist if price skeleton is there **/}
                        {!showPriceSkeleton && (
                            <Box
                                className="wishlist-wrapper"
                                onClick={(e) => {
                                    // stop click event from bubbling
                                    // to avoid user from clicking the underlying
                                    // product while the favourite icon is disabled
                                    e.preventDefault()
                                }}
                            >
                                {/* Animated Hearts */}
                                {wishlistAnimation === childIndex && <WishlistAnimation />}
                                <IconButtonWithRegistration
                                    data-testid="wishlist-button"
                                    // onMouseEnter={() => setIsHovered(true)}
                                    backgroundColor="gray.100"
                                    position="absolute"
                                    right="12px"
                                    top="12px"
                                    borderColor="transparent"
                                    _hover={{
                                        background: 'gray.100'
                                    }}
                                    borderRadius="50%"
                                    _focusVisible={{
                                        boxShadow: 'none'
                                    }}
                                    aria-label={
                                        isFavourite
                                            ? intl.formatMessage(
                                                  {
                                                      id: 'product_tile.assistive_msg.remove_from_wishlist',
                                                      defaultMessage:
                                                          'Remove {product} from wishlist'
                                                  },
                                                  {product: localizedProductName}
                                              )
                                            : intl.formatMessage(
                                                  {
                                                      id: 'product_tile.assistive_msg.add_to_wishlist',
                                                      defaultMessage: 'Add {product} to wishlist'
                                                  },
                                                  {product: localizedProductName}
                                              )
                                    }
                                    icon={
                                        isFavourite ? (
                                            <Image
                                                width="15px"
                                                alt="filled heart"
                                                height="15px"
                                                src={getUrl('UIImages/images/fill-heart-icon.svg')}
                                            />
                                        ) : (
                                            <Image
                                                src={getUrl('UIImages/images/empty-heart-icon.svg')}
                                                width="15px"
                                                alt="empty heart"
                                                height="15px"
                                            />
                                        )
                                    }
                                    productid={product?.productId || product?.id}
                                    productname={product?.name}
                                    category={product?.c_categoryName || ''}
                                    isplp={true}
                                    wishlistitems={wishlist?.customerProductListItems?.length || 0}
                                    onClick={async () => {
                                        if (!isFavouriteLoading.current) {
                                            isFavouriteLoading.current = true
                                            await onFavouriteToggle(
                                                !isFavourite,
                                                product?.productId || product?.id,
                                                product?.name,
                                                product?.c_categoryName
                                            )
                                            isFavouriteLoading.current = false
                                        }
                                    }}
                                    minWidth="31px"
                                    height="31px"
                                />
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Title */}
            <Box
                {...productUrlFormatted}
                className="product-title"
                _hover={{textDecoration: 'none'}}
            >
                <Text {...styles.title} className="product-name" letterSpacing="0.09px">
                    {selectedVariant?.name || localizedProductName}
                </Text>
            </Box>

            {/* Price */}
            <Box
                className="display-price-wrapper"
                marginRight="5px"
                display={priceData?.currentPrice ? 'block' : 'none'}
                sx={{
                    'b, p, span': {
                        fontSize: '14px',
                        fontWeight: '300',
                        letterSpacing: '0.07px'
                    }
                }}
            >
                {showPriceSkeleton ? (
                    <Stack
                        spacing={2}
                        data-testid="child-product-price-skeleton"
                        marginBottom="14px"
                    >
                        <ChakraSkeleton width="80px" height="20px" />
                    </Stack>
                ) : (
                    <DisplayPrice
                        priceData={priceData}
                        currency={product?.c_selectedCurrency || currency}
                        symbol={product?.c_selectedCurrencySymbol}
                        salePriceColor={salePriceColor}
                    />
                )}
            </Box>

            {/* Swatches / Selectors */}
            <Box>
                {variationAttributes
                    ?.map((attr) => ({
                        ...attr,
                        values: attr?.values?.filter((value) => {
                            const params = Object.fromEntries(
                                new URLSearchParams(value?.href?.split('?')?.[1] || '')
                            )

                            const selectedVariant =
                                product?.variants?.find((variant) =>
                                    Object.entries(params)?.every(
                                        ([key, val]) => variant?.variationValues?.[key] === val
                                    )
                                ) || false

                            return product?.c_allPdpLinks?.some(
                                (p) =>
                                    p.id === selectedVariant?.productId &&
                                    (p?.searchableIfUnavailableFlag || p?.orderable)
                            )
                        })
                    }))
                    ?.filter((attr) => attr.values.length)
                    ?.map(({id, name, values}, index) => {
                        const filteredSwatches = values

                        // Dropdown-based variations (size, length, etc.)
                        if (['size', 'letter', 'carat', 'length'].includes(id)) {
                            const value = selectedVariant?.variationValues
                                ? selectedVariant.variationValues[id]
                                : ''

                            return (
                                <Select
                                    key={`${id}-${index}`}
                                    value={product?.productId ? value : ''}
                                    {...formStyles.selectField}
                                    icon={
                                        <Image
                                            alt="Arrow"
                                            src={getUrl('UIImages/images/down-arrow-new.svg')}
                                            {...formStyles.selectIcon}
                                            width="13px"
                                            height="13px"
                                            marginRight="15px"
                                        />
                                    }
                                    onChange={(e) => {
                                        const selectedSize = values.find(
                                            (size) => size.value === e.target.value
                                        )
                                        const {variants = []} = product
                                        const selectedVariationValues = {
                                            ...variant?.variationValues,
                                            ...selectedVariant.productPromotions,
                                            [id]: selectedSize.value
                                        }
                                        if (variationAttributes?.length === 1) {
                                            setSelectableAttributeValue(e.target.value) // We want to change this only of there is 1 variation.
                                        }

                                        const selectedSku =
                                            variants.filter(({variationValues}) =>
                                                Object.keys(selectedVariationValues).every(
                                                    (key) =>
                                                        variationValues[key] ===
                                                        selectedVariationValues[key]
                                                )
                                            )?.[0] || {}

                                        setProduct({...product, ...selectedSku})
                                        setSelectedVariant(selectedSku)
                                        setShouldFetchPriceData(true)
                                    }}
                                    backgroundColor="white"
                                    marginTop="10px"
                                >
                                    <option value="" disabled>
                                        {id === 'carat' ? (
                                            <FormattedMessage
                                                defaultMessage="Select a carat size"
                                                id="select.a.carat.size"
                                            />
                                        ) : id === 'letter' ? (
                                            <FormattedMessage
                                                defaultMessage="Select a letter"
                                                id="select.a.letter"
                                            />
                                        ) : (
                                            <FormattedMessage
                                                defaultMessage="Select a size"
                                                id="select.a.size"
                                            />
                                        )}
                                    </option>
                                    {values?.map(({name, value, orderable}) => (
                                        <option key={value} value={value} disabled={!orderable}>
                                            {name}
                                        </option>
                                    ))}
                                </Select>
                            )
                        }

                        return (
                            <SwatchGroup
                                key={id}
                                ariaLabel={name}
                                flexDirection="row"
                                display="flex"
                                value={product?.productId ? selectableAttributeValue : ''}
                                groupProps={{gap: 1}}
                                onClick={(e) => e.preventDefault()}
                                handleChange={(value, isClick) => {
                                    if (isClick) {
                                        if (typeof value === 'string') {
                                            setShouldFetchPriceData(true)
                                            const variationParams = {[id]: value}
                                            const {variants = []} = product
                                            const selectedVariationValues = {
                                                ...variant?.variationValues,
                                                ...variationParams
                                            }
                                            setSelectableAttributeValue(value)

                                            const selectedSku =
                                                variants?.filter(({variationValues}) =>
                                                    Object.keys(selectedVariationValues)?.every(
                                                        (key) =>
                                                            variationValues?.[key] ===
                                                            selectedVariationValues[key]
                                                    )
                                                )?.[0] || {}
                                            setSelectedVariant(selectedSku)
                                            setProduct({...product, ...selectedSku})
                                            /** No need to set setChildProductSelection here as we are setting setShouldFetchPriceData to true above. **/
                                        } else {
                                            setHideMoreSwatches(false)
                                        }
                                    }
                                }}
                            >
                                {filteredSwatches
                                    ?.sort((a, b) => {
                                        if (id === 'letter') {
                                            return a?.name?.localeCompare(b.name, undefined, {
                                                sensitivity: 'base'
                                            })
                                        }
                                        if (a.value === initialVariationValue) return -1
                                        if (b.value === initialVariationValue) return 1
                                        return 0
                                    })
                                    ?.map(({name, value, image}, index) => {
                                        const content = image?.disBaseLink ? (
                                            <Box
                                                display={
                                                    index > 3 && hideMoreSwatches ? 'none' : 'block'
                                                }
                                                height="100%"
                                                width="100%"
                                                backgroundRepeat="no-repeat"
                                                backgroundSize="cover"
                                                data-id={id}
                                                backgroundColor={name.toLowerCase()}
                                                style={{
                                                    backgroundImage: `url('${image.disBaseLink}')`
                                                }}
                                            />
                                        ) : (
                                            name
                                        )

                                        const isOrderable = variants?.some(
                                            (v) =>
                                                v?.orderable &&
                                                v?.variationValues?.[selectableAttributeId] ===
                                                    value
                                        )

                                        return (
                                            <Swatch
                                                key={value}
                                                value={value}
                                                name={name}
                                                ispdp={false}
                                                isProductPartOfSet
                                                disabled={!isOrderable}
                                                selected={
                                                    selectedVariant?.variationValues?.[id] === value
                                                }
                                                variant="circle"
                                                swatchProps={{
                                                    display:
                                                        index > 2 && hideMoreSwatches
                                                            ? 'none'
                                                            : 'block'
                                                }}
                                            >
                                                {content}
                                            </Swatch>
                                        )
                                    })}

                                {filteredSwatches?.length > 3 && hideMoreSwatches && (
                                    <Button
                                        variant="unstyled"
                                        height="30px"
                                        width="auto"
                                        lineHeight="16px"
                                        border="none"
                                        padding="0"
                                        minHeight="initial"
                                        minWidth="initial"
                                        textDecoration="none"
                                        _hover={{textDecoration: 'none'}}
                                    >
                                        <Text
                                            as="span"
                                            color="black.100"
                                            fontSize="13px"
                                            lineHeight="100%"
                                            fontWeight="300"
                                        >
                                            <FormattedMessage
                                                defaultMessage="+ {count} more"
                                                values={{count: filteredSwatches.length - 3}}
                                                id="product_tile.button.more_swatches"
                                            />
                                        </Text>
                                    </Button>
                                )}
                            </SwatchGroup>
                        )
                    })}
            </Box>

            {!selectedVariant?.orderable && (
                <Text color="red.400" fontSize="14px" marginTop="10px">
                    Sold out
                </Text>
            )}
        </Box>
    )
}

ChildProduct.getTemplateName = () => 'child-product'

ChildProduct.propTypes = {
    match: PropTypes.object,
    addToCart: PropTypes.func,
    isBasketLoading: PropTypes.bool,
    setIsChildLoading: PropTypes.func,
    showAddedCta: PropTypes.bool,
    wishlist: PropTypes.object,
    customer: PropTypes.object,
    parentProduct: PropTypes.object,
    onFavouriteToggle: PropTypes.func,
    wishlistAnimation: PropTypes.any,
    index: PropTypes.any,
    setChildProductSelection: PropTypes.any,
    childProductSelection: PropTypes.any,
    product: PropTypes.any
}

export default ChildProduct
